import "./Selection.module.css";

import { arrayChunks, arrayEquals } from "common/utilities/core/array";
import { deepClone } from "common/utilities/core/object";
import { DEBOUNCE_TIME } from "common/utilities/timeout/timeout";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import type { AppDispatch } from "core/redux/store/configureStore.types";
import debounce from "debounce";
import React from "react";
import isEqual from "react-fast-compare";
import { connect } from "react-redux";
import type {
  EntitySequence as Sequence,
  SequenceAlign,
  SequenceType,
} from "sequenceAnalyser/api/sequenceAnalyserApiClient";
import SequenceResidueHoverTooltip from "sequenceAnalyser/components/Sequences/SequenceResidueHoverTooltip/SequenceResidueHoverTooltip";
import SequencesStyling from "sequenceAnalyser/components/Sequences/SequencesStyling/SequencesStyling";
import SequenceTableChunk from "sequenceAnalyser/components/Sequences/SequenceTableChunk/SequenceTableChunk";
import {
  clearSelection,
  createSelection,
  sequenceTableLoaded,
  updateScrollPosition,
} from "sequenceAnalyser/redux/actions/sequenceAnalyserActions";
import type { ExportToPNGStatus, ScrollPosition } from "sequenceAnalyser/redux/reducers/sequenceAnalyserReducer.types";
import type { ColumnsLetterOccurrences } from "sequenceAnalyser/utilities/colorCalculations/colorCalculations";
import type { SelectedColorScheme } from "sequenceAnalyser/utilities/colors";
import { featuresForResidue } from "sequenceAnalyser/utilities/features/features";
import { moveSequenceTo } from "sequenceAnalyser/utilities/move/move";
import {
  calculateVisibleResiduesChunks,
  DEFAULT_VISIBLE_RESIDUES_CHUNKS,
  sequencesPerChunk,
} from "sequenceAnalyser/utilities/screen/screen";
import type { SequenceSearchMatch, SequencesSearch } from "sequenceAnalyser/utilities/search/search";
import type { SequenceSelection } from "sequenceAnalyser/utilities/selection/selection";
import {
  clearResiduesSelection,
  initSequenceResiduesSelection,
  markResiduesFromSelectionAsSelected,
} from "sequenceAnalyser/utilities/selection/selection";
import type {
  FeatureDefinitions,
  Residue,
  SequenceAnalyserTableView,
  SequenceAnalyserVisualisation,
} from "sequenceAnalyser/utilities/sequenceAnalyser.types";
import { hasAnyFeatureDefinitionVisible } from "sequenceAnalyser/utilities/sequenceAnalyser/sequenceAnalyser";

export type HandleResidueHover = (residue?: Residue) => void;

export function handleZoomableElementsChange(zoomLevel: number) {
  // Adjust scale of elements according to zoom level
  const zommableElements = document.getElementsByClassName("zoomable");

  if (zommableElements) {
    const transformStyle = zoomLevel === 100 ? "none" : `scale(${(zoomLevel || 100) / 100})`;
    Array.from(zommableElements).forEach((element) => {
      (element as HTMLElement).style.transform = transformStyle;
    });
  }
}

function sequenceTableChunkAlwaysVisible(sequences: SequenceAlign[], selection: SequenceSelection) {
  if (selection.ids.length === 0 || (selection.positionStart === undefined && selection.positionEnd === undefined))
    return false;
  return sequences.some((s) => selection.ids.includes(s.id));
}

interface PropsFromState {
  zoomLevel: number;
  searchActiveMatch?: SequenceSearchMatch;
  showHoveredResidueTooltip: boolean;
  sidebarPixelWidth: number;
  selection: SequenceSelection;
  visualisations: SequenceAnalyserVisualisation[];
  labelComposition: string[];
  exportToPNGStatus: ExportToPNGStatus;
}

export interface ComponentProps extends PropsFromState {
  sequences: Sequence[];
  alignedSequences: SequenceAlign[];
  featureDefinitions: FeatureDefinitions;
  colors?: SelectedColorScheme;
  dispatchCreateSelection: (selection: SequenceSelection) => void;
  dispatchClearSelection: () => void;
  dispatchUpdateScrollPosition: (scrollPosition?: ScrollPosition) => void;
  dispatchSequenceTableLoaded: () => void;
  columnsLetterOccurrences: ColumnsLetterOccurrences;
  view: SequenceAnalyserTableView;
  onAlignedSequencesChange: (sequences: SequenceAlign[]) => void;
  search: SequencesSearch;
  alignmentVersion: number;
  expandedFeaturesForSequences: number[];
  oppositeStrands: number[];
  sessionSequenceType: SequenceType;
  maskingResidue: string;
  activeAlignment: boolean;
}

interface ComponentState {
  hoveredResidue?: Residue;
  visibleResiduesChunks: number[];
}

class SequenceTable extends React.Component<ComponentProps, ComponentState> {
  private horizontalScrollPosition = 0;

  private debouncedHandleScroll: EventListenerOrEventListenerObject & ReturnType<typeof debounce>;

  private debouncedHandleResidueHover: HandleResidueHover & ReturnType<typeof debounce>;

  constructor(props: ComponentProps) {
    super(props);

    this.state = {
      visibleResiduesChunks: DEFAULT_VISIBLE_RESIDUES_CHUNKS,
    };

    // Lower than 30ms generates many calls while the user is still scrolling
    this.debouncedHandleScroll = debounce(() => this.handleScroll(), 30);

    this.debouncedHandleResidueHover = debounce(
      (residue?: Residue) => this.setState({ hoveredResidue: residue }),
      DEBOUNCE_TIME.sequenceTableResidueHover,
    );
  }

  componentDidMount() {
    const { dispatchSequenceTableLoaded } = this.props;

    this.bindSequenceSelection();

    dispatchSequenceTableLoaded();

    window.addEventListener("resize", this.debouncedHandleScroll);
    window.addEventListener("scroll", this.debouncedHandleScroll);
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { selection, zoomLevel, expandedFeaturesForSequences, alignedSequences } = this.props;
    const { visibleResiduesChunks } = this.state;

    if (zoomLevel !== prevProps.zoomLevel) {
      this.handleScroll(true);
    }

    if (alignedSequences.length !== prevProps.alignedSequences.length) {
      this.handleZoomableElementsChange();
    }

    // Ensure we update the selection when the user perform some actions:
    // - scrolling
    // - toggling features
    // - insert/delete gaps using the keyboard
    // Many other actions like inserting/removing sequences clear the selection and are handled in SequenceAnalyser component.
    if (
      !arrayEquals(visibleResiduesChunks, prevState.visibleResiduesChunks) ||
      !arrayEquals(expandedFeaturesForSequences, prevProps.expandedFeaturesForSequences) ||
      zoomLevel !== prevProps.zoomLevel ||
      selection.positionStart !== prevProps.selection.positionStart ||
      selection.positionEnd !== prevProps.selection.positionEnd
    ) {
      markResiduesFromSelectionAsSelected(selection);
    }
  }

  componentWillUnmount() {
    clearResiduesSelection();

    window.removeEventListener("resize", this.debouncedHandleScroll);
    window.removeEventListener("scroll", this.debouncedHandleScroll);

    this.debouncedHandleResidueHover.clear();
  }

  // On scroll, calculate which residues chunk is currently visible
  handleScroll = (forceCalculation?: boolean) => {
    const { zoomLevel, sidebarPixelWidth, dispatchUpdateScrollPosition } = this.props;

    const { visibleResiduesChunks } = this.state;

    // Save latest scroll position
    dispatchUpdateScrollPosition({ x: window.scrollX, y: window.scrollY });

    // Return if not horizontal scrolling
    const newHorizontalScrollPosition = deepClone(window.scrollX);

    if (this.horizontalScrollPosition === newHorizontalScrollPosition && forceCalculation !== true) {
      return;
    }

    // Set new scroll position
    this.horizontalScrollPosition = newHorizontalScrollPosition;

    // Init variables
    const newVisibleResiduesChunks = calculateVisibleResiduesChunks(
      this.horizontalScrollPosition,
      zoomLevel,
      sidebarPixelWidth,
    );

    // Only update the state if the visible chunks have changed
    if (!isEqual(visibleResiduesChunks, newVisibleResiduesChunks)) {
      this.setState({ visibleResiduesChunks: newVisibleResiduesChunks });
    }

    this.handleZoomableElementsChange();
  };

  handleZoomableElementsChange = () => {
    const { zoomLevel } = this.props;

    handleZoomableElementsChange(zoomLevel);
  };

  handleResidueHover: HandleResidueHover = (residue?: Residue) => {
    if (residue) {
      this.debouncedHandleResidueHover(residue);
    } else {
      this.debouncedHandleResidueHover(undefined);
      this.setState({ hoveredResidue: residue });
    }
  };

  handleDragNDrop = (from: number, to: number) => {
    const { alignedSequences, onAlignedSequencesChange } = this.props;
    onAlignedSequencesChange(moveSequenceTo(alignedSequences, from, to));
  };

  bindSequenceSelection() {
    const { dispatchCreateSelection, dispatchClearSelection, selection } = this.props;

    initSequenceResiduesSelection(selection, (newSelection) => {
      if (newSelection) {
        dispatchCreateSelection(newSelection);
      } else {
        dispatchClearSelection();
      }
    });
  }

  render() {
    const {
      sequences,
      alignedSequences,
      featureDefinitions,
      colors,
      columnsLetterOccurrences,
      view,
      zoomLevel,
      search,
      searchActiveMatch,
      alignmentVersion,
      showHoveredResidueTooltip,
      sidebarPixelWidth,
      expandedFeaturesForSequences,
      oppositeStrands,
      visualisations,
      labelComposition,
      sessionSequenceType,
      maskingResidue,
      activeAlignment,
      selection,
      exportToPNGStatus,
    } = this.props;
    const { hoveredResidue } = this.state;

    const hasColors = colors !== undefined;
    const hasFeatures = hasAnyFeatureDefinitionVisible(featureDefinitions);
    const hasVisualisations = visualisations.length > 0;

    const alignedSequencesChunks = arrayChunks<SequenceAlign>(
      alignedSequences,
      sequencesPerChunk(hasColors, hasFeatures),
    );

    return (
      <div onMouseLeave={() => this.handleResidueHover(undefined)}>
        <SequencesStyling hasColors={hasColors} hasFeatures={hasFeatures} hasVisualisations={hasVisualisations}>
          {showHoveredResidueTooltip && hoveredResidue && (
            <SequenceResidueHoverTooltip
              residue={hoveredResidue}
              colors={colors}
              columnsLetterOccurrences={columnsLetterOccurrences}
              features={featuresForResidue(sequences, hoveredResidue)}
              onResidueHover={this.handleResidueHover}
              sessionSequenceType={sessionSequenceType}
              activeAlignment={activeAlignment}
            />
          )}
          {alignedSequencesChunks.map((chunk, i) => (
            <SequenceTableChunk
              key={`sequence-table-chunk-${i}`}
              chunkId={i}
              sequences={sequences}
              alignedSequences={alignedSequences}
              alignedSequencesChunk={chunk}
              featureDefinitions={featureDefinitions}
              hoveredResidue={Boolean(hoveredResidue)}
              onResidueHover={this.handleResidueHover}
              colors={colors}
              columnsLetterOccurrences={columnsLetterOccurrences}
              view={view}
              zoomLevel={zoomLevel}
              onDragNDrop={this.handleDragNDrop}
              search={search}
              searchActiveMatch={searchActiveMatch}
              alignmentVersion={alignmentVersion}
              sidebarPixelWidth={sidebarPixelWidth}
              expandedFeaturesForSequences={expandedFeaturesForSequences}
              oppositeStrands={oppositeStrands}
              labelComposition={labelComposition}
              onVisibilityChanged={this.handleZoomableElementsChange}
              maskingResidue={maskingResidue}
              windowInnerSize={`${window.innerWidth}*${window.innerHeight}`}
              alwaysVisible={exportToPNGStatus === "processing" || sequenceTableChunkAlwaysVisible(chunk, selection)}
            />
          ))}
        </SequencesStyling>
      </div>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  zoomLevel: state.sequenceAnalyser.zoomLevel,
  searchActiveMatch: state.sequenceAnalyser.searchActiveMatch,
  showHoveredResidueTooltip: state.sequenceAnalyser.settings.showHoveredResidueTooltip,
  sidebarPixelWidth: state.sequenceAnalyser.sidebarPixelWidth,
  selection: state.sequenceAnalyser.selection,
  visualisations: state.sequenceAnalyser.settings.visualisations,
  labelComposition: state.sequenceAnalyser.settings.labelComposition, // the switchable elements to be included with the sequence label
  exportToPNGStatus: state.sequenceAnalyser.exportToPNGStatus,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  dispatchCreateSelection: (selection: SequenceSelection) => dispatch(createSelection(selection)),
  dispatchClearSelection: () => dispatch(clearSelection()),
  dispatchUpdateScrollPosition: (scrollPosition?: ScrollPosition) => dispatch(updateScrollPosition(scrollPosition)),
  dispatchSequenceTableLoaded: () => dispatch(sequenceTableLoaded()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SequenceTable);
